import { render } from 'react-dom'
import './styles.css'
import * as THREE from 'three'
import { useRef, Suspense, useEffect, useState, useMemo } from 'react'
import { Canvas, useFrame, useThree } from '@react-three/fiber'
import { Center, Instances, Instance, OrbitControls } from '@react-three/drei'
import * as Tone from 'tone'
import Colors from './Colors'
import { AsciiEffect } from 'three-stdlib'
import { LayerMaterial, Color, Noise } from 'lamina/vanilla'
let lerp = THREE.MathUtils.lerp
let randI = (i) => Math.floor(i * fxrand())
let rand = (arr) => arr[randI(arr.length)]
function shuffle(array) {
  let currentIndex = array.length, randomIndex;
  while (currentIndex != 0) {
    randomIndex = Math.floor(fxrand() * currentIndex);
    currentIndex--;
    [array[currentIndex], array[randomIndex]] = [
      array[randomIndex], array[currentIndex]];
  }
  return array;
}
let ascii = randI(100) > 85
const ci = ascii ? rand([21, 17, 15, 14, 13, 10, 6, 0, 0, 0, 2]) : randI(Colors.length)
let cols = Colors[ci]
const bgColor = cols.shift()
shuffle(cols)
let colDir = rand([0, 1, 1])
let octs = [2, 3, 4]
let scales = ['C', 'D', 'E', 'F', 'G', 'A', 'C', 'D', 'E', 'F', 'G', 'A', 'C', 'D', 'E', 'F', 'G', 'A']
new Array(scales.length * 0.5).fill().forEach(() => scales.splice(randI(scales.length), 1))
new Array(rand([0, 0, 1])).fill().forEach(() => rand([1, 0]) ? octs.pop() : octs.shift())

const feats = window.$fxhashFeatures = {
  amplitude: 18 + randI(20),
  radians: 4 + randI(4),
  colorDir: colDir ? '⎯' : '|',
  attack: (4 + randI(6)) * 0.01,
  sustain: (5 + randI(10)) * 0.006,
  release: (1 + randI(10)) * 0.5,
  notes: scales.join(''),
  octaves: octs.join(''),
  // colors: ci,
  ascii
}
const steps = 400;
const totalRibbons = 17
let oneRib = 1 + randI(16)
let ribbons = [oneRib, totalRibbons - oneRib]
let twirls = 2
let lerpedCols = []
let twirlScale = 1
let col1 = new THREE.Color(rand(cols))
let col2 = null

new Array(12).fill().forEach((_, ci) => {
  col2 = new THREE.Color(rand(cols))
  let steps = rand([1, 1, 1, 1, 1, 1, 1, 1, 2, 5, 7, 11, 13, 15, 20])
  lerpedCols.push(...new Array(steps).fill().map((_, i) =>
    col1.clone().lerp(col2, (i) / steps)
  ))
  col1 = col2.clone()
})
let stopTimeout = null
let first = true

export default function App() {
  const start = () => {
    clicked = true
    if (!started && (loaded === instNotes.length)) {
      if (stopTimeout) {
        clearTimeout(stopTimeout)
      }
      genMelody()
      Tone.start();
      Tone.Transport.start();
      part.start()
      rotSpeed = 2
      if (!first) {
        rotSpeed = rand([0.5, 1, 1, 1, 2, 2, 2, 2, 2, 2, 2, 4, 8])
        Tone.Transport.bpm.linearRampTo(30 + randI(melody.length * 2), 3, '+0.01')
        let a = [...arpNotes]
        a[randI(a.length)] = rand(arpNotes)
        melody = [...a, ...[...a].reverse()]
      }
      first = false
    } else if ((loaded === instNotes.length)) {
      Tone.Transport.bpm.linearRampTo(20, Tone.Transport.bpm.value*0.02, '+0.01')
      clearTimeout(stopTimeout)
      stopTimeout = setTimeout(() => {
        part.stop()
      }, ((Tone.Transport.bpm.value*0.02)*1000)*1.5)
    }
    started = !started
  }
  let z = 0.2 + randI(3)
  return (
    <div id="main" onClick={() => start()}>
      <Canvas flat={true} dpr={[1, 2]} camera={{ near: 0.001, far: 10000, position: [rand([1, -1]) * z, rand([1, -1]) * z, -48], fov: 115 }}>
        <Stuff />
        <OrbitControls enableRotate={false} enablePan={false} minDistance={2} maxDistance={80} zoomSpeed={1.2} />
        <ambientLight intensity={0.5} />
        <directionalLight position={[-10, 10, -5]} intensity={0.65} />
        <directionalLight position={[-10, 10, 5]} intensity={0.25} />
        <directionalLight position={[-10, 20, 0]} intensity={0.35} />
        <directionalLight position={[0, -5, -10]} intensity={0.45} />
        {!feats.ascii && <Back />}
        {feats.ascii && <Ascii invert={false} />}
      </Canvas>
    </div>
  )
}
let s = (ascii ? 1.5 : 1) * 0.12
let geo = new THREE.BoxBufferGeometry(s * 8, s * 0.5, s * 0.5)
let started = false;
let rot = 0
let rotDir = rand([1, -1])
function Stuff() {
  const all = useRef()
  useFrame(() => {
    all.current.rotation.x = lerp(all.current.rotation.x, rot * rotDir, 0.1)
    twirlScale = lerp(twirlScale, 1, 0.1)
    maxTime = (rot * 2)
    if (time < maxTime) {
      time = lerp(time, maxTime, 0.15)
    }
  })
  return (
    <Center>
      <group rotation={[0, 0, rand([Math.PI * 0.5 + (randI(3) * 0.05)]) * rand([1, -1])]}>
        <group ref={all}>
          <Instances limit={10000} geometry={geo}>
            <meshPhongMaterial depthTest={false} />
            {new Array(twirls).fill().map((_, o) =>
              new Array(ribbons[o]).fill().map((_, n) =>
                <group position={[o * (280 / feats.radians), 0, 0]} key={n}>
                  <group position={[n * 2.5, 0, 0]} >
                    {new Array(steps).fill().map((_, step) => <Thing step={step} key={(steps * n) + step} n={n} r={feats.radians} o={o} />)}
                  </group>
                </group>
              )
            )}
          </Instances>
        </group>
      </group>
    </Center>
  )
}

let colorA = new THREE.Color(bgColor).convertSRGBToLinear()
const bgMat = new LayerMaterial({
  depthTest: false,
  side: THREE.DoubleSide,
  layers: [
    new Color({ color: colorA }),
    new Noise({ mapping: 'local', type: 'white', scale: 1000, colorA: 'white', colorB: 'black', mode: 'subtract', alpha: 0.3 })
  ]
})

let xStep = rand([-0.04])
let time = 0
let maxTime = 0
let ribbonOffset = randI(16)
let sameRad = rand([true, false, false])
let skew = rand([2])
function Thing({ step, n, r, o }) {
  let a = feats.amplitude * 0.6
  const s = useRef(1)

  const ref = useRef();
  let z = sameRad ? 0.8 : (o ? 1.2 : 0.5)
  let getX = () => step * xStep * 19.5
  let getY = () => Math[rotDir ? 'cos' : 'sin'](((step % steps) / steps) * Math.PI * r) * a * z * ((step * skew) / steps)
  let getZ = () => Math[rotDir ? 'sin' : 'cos'](((step % steps) / steps) * Math.PI * r) * a * z * ((step * skew) / steps)
  let sc = (t = 0) => {
    let i = ((o * ribbons[o]) + n + ribbonOffset) % 16
    let mRes = []
    randModes.forEach((m, j) => mRes[j] = modes[m](t + (j * 33), step, i))
    return Math.max(...mRes) * twirlScale * 1.5
  }
  useFrame(() => {
    s.current = lerp(s.current, sc(time), 0.8)
    ref.current.scale.setScalar(s.current)
  })
  return (
    <Instance
      ref={ref}
      rotation={[0, 0, (!n || n === (ribbons[o] - 1)) ? Math.PI * 0 : 0]}
      position={[getX(), getY(), getZ()]}
      scale={[sc(time), sc(time), sc(time)]}
      color={feats.ascii ? '#0a0a0a' : lerpedCols[(colDir ? step : n) % lerpedCols.length]}
    />
  )
}
function Back() {
  const ref = useRef();
  useFrame(() => {
    ref.current.rotation.y = lerp(ref.current.rotation.y, rot * rotDir, 0.1)
  })
  let s = 160
  return (
    <group ref={ref}>
      <mesh material={bgMat}>
        <octahedronBufferGeometry args={[s, 6]} />
      </mesh>
    </group>
  )
}
let filters = []
let pans = []
const instNotes = ['A2', 'A3', 'C3', 'D3', 'E3', 'G2', 'G3']
let pansAmt = instNotes.length
let arpNotes = []
octs.forEach((o) =>
scales.forEach((s) =>
arpNotes.push(s + o)
)
)
let melody = [...arpNotes, ...[...arpNotes].reverse()]

let spread = 1.6
new Array(pansAmt).fill().map((_, i) => {
  pans[i] = new Tone.Panner((((spread / pansAmt) * i) - (spread * 0.5)) + (spread / (pansAmt * 2))).toDestination()
  filters[i] = new Tone.Filter(1800 + (10 * randI(10)), "bandpass", -12).connect(pans[i])
  filters[i].connect(pans[i])
})

const insts = {};
let urls = {}
let loaded = 0
let clicked = false
instNotes.forEach((n) => urls['C4'] = "./teropa/scale-" + n + ".mp3")
instNotes.forEach((n, i) => {
  insts[n] = new Tone.Sampler(
    urls,
    () => {
      loaded++;
      if (loaded === instNotes.length && clicked) {
        Tone.start();
        Tone.Transport.start();
        part.start()
        first = false
      }
    })
  insts[n].attack = feats.attack
  insts[n].release = feats.release
  insts[n].volume.value = 22
  insts[n].connect(filters[i])
})

let loopTime = 0
let c = melody.length * 0.5
let notes = new Array(melody.length * 0.5).fill().map((_, i) => {
  let n = {}
  n.dur = 0.142 * 1
  n.time = loopTime
  n.i = i
  n.vel = 48 + (fxrand() * 10)
  n.inst = rand(instNotes)
  loopTime += n.dur
  return n
})
let melodyMod, melodyDiv, melodyStep, melodyPump, pumpSpeed, rotSpeed, oppMod, pumps,opp;
let genMelody = () => {
  melodyMod = rand([2, 4, 6, 8])
  oppMod = rand([2, 3, 4, 5, 6, 7, 8])
  opp = rand([0.25,0.5,0.75,0.33,0.125])
  melodyDiv = rand([0.125, 0.25, 0.5, 1]) * 0.125 * 0.5
  melodyStep = rand([1, 1, 1, 1, 1, 2, 3])
  melodyPump = rand([8, 12, 16, 20, 24, ...new Array(40).fill(0)])
  pumpSpeed = rand([0.5, 0.75, 1, 1.25, 1.5]) * 0.33
  pumps = rand([2, 4, 6, 8, 10, 12])
}
genMelody()
let loops = 0
let part = new Tone.Part(((time, { dur, vel, i, inst }) => {
  c++
  if (i === 0) {
    loops++;
    if (loops % 4 === 0) {
      genMelody()
    }
  }
  twirlScale = 1.1
  rot += ((1 / arpNotes.length) * Math.PI * rotSpeed)
  let n = melody[c % melody.length]
  let n2 = melody[(c + Math.floor((melody.length * oppMod))) % melody.length]
  let n3 = melody[Math.abs(((Math.floor(c * melodyDiv) + ((c * melodyStep) % melodyMod))) % melody.length)]
  let noise = () => rand([0.01, 0.02, 0.03, 0.04, 0.05]) * rand([1, -1]) * 0.1
  let s1 = 1 / (Tone.Frequency(n).toMidi() * 4)
  let s2 = 1 / (Tone.Frequency(n2).toMidi() * 4)
  let s3 = 1 / (Tone.Frequency(n3).toMidi() * 4)
  insts[inst].triggerAttackRelease(n, (dur * feats.sustain) + noise(), time, (vel * 1 * s1) + noise());
  if (i % oppMod === 0) {
    insts[inst].triggerAttackRelease(n2, ((dur * feats.sustain)) + noise(), time, vel * 1 * s2);
  }
  if (i % melodyPump === 0) {
    new Array(pumps).fill().map((_, i) =>
      insts[inst].triggerAttackRelease(n2, ((dur * feats.sustain * 1.5)) + noise(), time + (dur * (0.5 + ((i + 1) * pumpSpeed))), vel * 0.5 * s2)
    )
  }
  insts[inst].triggerAttackRelease(n3, (dur * feats.sustain) + noise(), time, vel * 1 * s3);
}), notes)
part.loop = true
part.loopEnd = loopTime
Tone.Transport.bpm.value = 30 + randI(melody.length * 1.5)
document.getElementById('root').style.background = bgColor
render(
  <Suspense fallback={null}>
    <App />
  </Suspense>,
  document.getElementById('root')
)
let chars = [
  ' .:+*=%@▓█',
  ' .:+*=%@#█',
  ' .:+*=%@Å▓',
  ' .:+*=%@Ä▓',
  ' .:+*=%@Ä█',
  ' .:+*=%@Æ▓',
  ' .:+*=%@Ö▓',
  ' .:+*=%@Ø▓',
  ' .:+*=%#@▓',
  ' .:+*=%Ö@█',
  ' .:-+*=%Ö@',
  ' .:-+#▓▒░',
]
function Ascii({ renderIndex = 1, ...options }) {
  const { size, gl, scene, camera } = useThree()
  const effect = useMemo(() => {
    const effect = new AsciiEffect(gl, rand(chars), options)
    effect.domElement.style.position = 'absolute'
    effect.domElement.style.top = '0px'
    effect.domElement.style.left = '0px'
    effect.domElement.style.bottom = '0px'
    effect.domElement.style.color = rand(cols)
    effect.domElement.style.backgroundColor = bgColor
    effect.domElement.style.pointerEvents = 'none'
    return effect
  }, [options.invert])

  useEffect(() => {
    gl.domElement.parentNode.appendChild(effect.domElement)
    return () => gl.domElement.parentNode.removeChild(effect.domElement)
  }, [effect])

  useEffect(() => {
    effect.setSize(size.width, size.height)
  }, [effect, size])

  useFrame((state) => {
    effect.render(scene, camera)
  }, renderIndex)

  return null
}

let modes = [
  (t, c, r) => Math.cos((t * 2) + Math.sqrt(c * r * 0.05)) + .5,
  (t, c, r) => r / 4 & (c - 8) / r + t * 1,
  (t, r, c) => ((c + (r + t * 18) ^ c - (r + t * 18)) % 13 & 1) - .15,
  (t, r, c) => ((c + (r + t * 11) ^ c - (r + t * 11)) % 13 & 1) - .15,
  (t, r, c) => ((c + (r + t * 11) ^ c - (r + t * 11)) % 11 & 1) - .15,
  (t, c, r) => Math.sin(c / 3 + (r / 5 + t * 0.5) * 4) * 1.2,
  (t, c, r) => Math.sin(c / 11 + (r / 5 + t * 0.5) * 4),
  (t, c, r) => Math.cos(c / 2 + (r / 5 + t * 0.5) * 4) * 1,
  (t, c, r) => [8, 5, 3, 1].reduce((a, b) => r > a + a / 2 * Math.cos(c / a + t * (a * 0.2)) ? -a / 5 : b) * 1.5,
  (t, c, r) => [7, 5, 3, 1].reduce((a, b) => r > a + a / 2 * Math.cos(c / a + (t * 0.5) * a) ? -a / 3 : b) * 1.5,
]
let randModes = new Array(2 + randI(2)).fill().map(() => randI(modes.length))
console.log('%c🧬🧬 @ramioso 2022-05-22 🧬🧬', 'color: green');


